import { 
  PlatformAdapter, 
  PermissionStatus, 
  NotificationOptions, 
  NotificationChannel,
  PendingNotification,
  DeliveredNotification,
  ReceivedNotification
} from '../types';

// **重要：定义 window 上的接口，避免 TypeScript 报错**
declare global {
  interface Window {
    electronNotification?: {
      invoke: (channel: string, ...args: any[]) => Promise<any>;
      on: (channel: string, listener: (event: any, ...args: any[]) => void) => () => void;
    };
    // 可能还需要 electronCalendar，如果其他地方也需要访问
    electronCalendar?: any; 
  }
}

/**
 * Electron 通知适配器
 * 使用 Electron 原生通知 API 支持桌面系统通知
 */
export class ElectronAdapter implements PlatformAdapter {
  private pendingNotifications: Map<string, { options: NotificationOptions, timer?: NodeJS.Timeout }> = new Map();
  private deliveredNotifications: Map<string, { notification: any, deliveredAt: Date }> = new Map();
  private removeClickSubscription?: () => void;
  private removeActionSubscription?: () => void;
  private removeCloseSubscription?: () => void;

  constructor() {
    this.setupIpcListeners();
    console.log('[ElectronAdapter] Initialized.');
  }

  /**
   * 设置 IPC 监听器 (使用暴露的 API)
   */
  private setupIpcListeners() {
    if (!window.electronNotification || typeof window.electronNotification.on !== 'function') {
      console.warn('[ElectronAdapter] Electron Notification API (on) not found on window.');
      return;
    }
    console.log('[ElectronAdapter] Setting up IPC listeners via window.electronNotification.on');

    // 清理旧监听器（如果存在）
    this.removeClickSubscription?.();
    this.removeActionSubscription?.();
    this.removeCloseSubscription?.();

    // 监听通知点击事件
    this.removeClickSubscription = window.electronNotification.on('notification-clicked', (event: any, data: any) => {
      console.log('[ElectronAdapter] Received IPC event: notification-clicked', data);
      if (data && data.id) {
        this.notificationActionHandler({ id: data.id, source: 'local', actionId: 'click' });
      }
    });

    // 监听通知操作事件
    this.removeActionSubscription = window.electronNotification.on('notification-action', (event: any, data: any) => {
       console.log('[ElectronAdapter] Received IPC event: notification-action', data);
       if (data && data.id) {
         this.notificationActionHandler({
           id: data.id,
           source: 'local',
           actionId: data.type,
           data: { action: data.type, value: data.value, index: data.index }
         });
       }
    });

    // 监听通知关闭事件
    this.removeCloseSubscription = window.electronNotification.on('notification-closed', (event: any, data: any) => {
       console.log('[ElectronAdapter] Received IPC event: notification-closed', data);
       if (data && data.id && this.deliveredNotifications.has(data.id)) {
         this.deliveredNotifications.delete(data.id);
       }
    });
  }

  /**
   * 请求/检查通知权限 (使用暴露的 API)
   */
  async requestPermission(): Promise<PermissionStatus> {
    if (!this.isElectronAvailable()) {
      return 'denied';
    }
    try {
      const result = await window.electronNotification!.invoke('check-notification-permission');
      return result.status || 'default';
    } catch (error) {
      console.error('[ElectronAdapter] Error checking permission via IPC:', error);
      return 'denied';
    }
  }

  async checkPermission(): Promise<PermissionStatus> {
    return this.requestPermission();
  }

  /**
   * 打开系统通知设置 (使用暴露的 API)
   */
  async openSystemSettings(): Promise<void> {
    if (!this.isElectronAvailable()) {
      console.warn('[ElectronAdapter] Cannot open settings: Electron API not available.');
      return;
    }
    try {
      await window.electronNotification!.invoke('open-notification-settings');
    } catch (error) {
      console.error('[ElectronAdapter] Error opening notification settings via IPC:', error);
    }
  }

  async scheduleLocalNotification(options: NotificationOptions): Promise<string> {
     if (!this.isElectronAvailable()) {
       throw new Error('Electron not available for notifications');
     }
     const id = options.id || Math.random().toString(36).substring(2, 10);
     if (options.schedule?.at) {
       const delay = options.schedule.at.getTime() - Date.now();
       if (delay > 0) {
         this.pendingNotifications.set(id, { options });
         const timer = setTimeout(() => {
           this.showElectronNotification(id, options).catch(err => console.error('Error showing delayed notification:', err));
           this.pendingNotifications.delete(id);
         }, delay);
         this.pendingNotifications.set(id, { options, timer });
         return id;
       }
     }
     return this.showElectronNotification(id, options);
  }

  /**
   * 显示 Electron 通知 (使用暴露的 API)
   */
  private async showElectronNotification(id: string, options: NotificationOptions): Promise<string> {
    if (!this.isElectronAvailable()) {
      throw new Error('Electron API not available');
    }
    try {
      // 构造传递给主进程的选项
      const notificationOptions = {
        id,
        title: options.title,
        body: options.body || '',
        subtitle: options.extra?.subtitle || '',
        silent: options.sound === false,
        hasReply: !!options.extra?.hasReply,
        replyPlaceholder: options.extra?.replyPlaceholder || '',
        closeButtonText: options.extra?.closeButtonText || '',
        icon: options.icon,
        actions: options.actions?.map(action => ({
          type: action.id, // type 对应 Electron 的 action type
          text: action.title
        })),
        customData: options.extra // 传递 extra 数据
      };

      const result = await window.electronNotification!.invoke('show-native-notification', notificationOptions);

      if (result.error) { throw new Error(result.error); }

      // 记录已递送通知 (本地记录，因为主进程无法持久化 Notification 对象)
      this.deliveredNotifications.set(id, {
         notification: { ...options }, // 只存储原始选项
         deliveredAt: new Date()
       });

      return id;
    } catch (error) {
      console.error('[ElectronAdapter] Error showing notification via IPC:', error);
      throw error;
    }
  }

  private notificationActionHandler(notification: ReceivedNotification) {
    try {
      import('../NotificationService').then(({ NotificationService }) => {
        const service = NotificationService.getInstance();
        service.triggerActionSubscriptions(notification);
      }).catch(error => {
        console.error('[ElectronAdapter] Error importing NotificationService:', error);
      });
    } catch (error) {
      console.error('[ElectronAdapter] Notification action handler error:', error);
    }
  }

  async cancelLocalNotification(id: string): Promise<void> {
     if (!this.isElectronAvailable()) { return; }
     try {
       if (this.pendingNotifications.has(id)) {
          const pending = this.pendingNotifications.get(id);
          if (pending?.timer) { clearTimeout(pending.timer); }
          this.pendingNotifications.delete(id);
       }
       await window.electronNotification!.invoke('cancel-notification', { id });
       if (this.deliveredNotifications.has(id)) {
          this.deliveredNotifications.delete(id);
       }
     } catch (error) { 
       console.error('[ElectronAdapter] Cancel notification error:', error);
       // 不抛出错误，避免中断调用链
     } 
  }

  async cancelAllLocalNotifications(): Promise<void> {
     if (!this.isElectronAvailable()) { return; }
     try {
        for (const [id, pending] of this.pendingNotifications.entries()) {
          if (pending.timer) { clearTimeout(pending.timer); }
        }
        this.pendingNotifications.clear();
        await window.electronNotification!.invoke('cancel-all-notifications');
        this.deliveredNotifications.clear(); 
     } catch (error) { 
       console.error('[ElectronAdapter] Cancel all notifications error:', error);
     }
  }

  async getPendingNotifications(): Promise<PendingNotification[]> {
    return Array.from(this.pendingNotifications.entries()).map(([id, { options }]) => ({
      id,
      title: options.title,
      body: options.body || '',
      schedule: options.schedule,
      extra: options.extra
    }));
  }

  async getDeliveredNotifications(): Promise<DeliveredNotification[]> {
     if (!this.isElectronAvailable()) { return []; }
     try {
       const notifications = await window.electronNotification!.invoke('get-delivered-notifications');
       if (Array.isArray(notifications)) {
         return notifications.map(n => ({
           id: n.id,
           title: n.title || '',
           body: n.body || '',
           extra: n.data,
           deliveredAt: new Date(n.deliveredAt || Date.now())
         }));
       }
     } catch (error) { 
       console.error('[ElectronAdapter] Get delivered notifications error via IPC:', error);
     }
     return []; // 如果 IPC 失败，返回空（主进程不持久化通知）
  }

  async removeDeliveredNotifications(ids: string[]): Promise<void> {
    if (!this.isElectronAvailable() || !ids.length) { return; }
    try {
      await window.electronNotification!.invoke('remove-delivered-notifications', { ids });
      for (const id of ids) { this.deliveredNotifications.delete(id); }
    } catch (error) { 
       console.error('[ElectronAdapter] Remove delivered notifications error:', error);
    }
  }

  async createChannel(channel: NotificationChannel): Promise<void> {
    console.log('[ElectronAdapter] Channels not supported.');
    return;
  }

  /**
   * 检查 Electron API 是否可用
   */
  private isElectronAvailable(): boolean {
    return !!window.electronNotification && typeof window.electronNotification.invoke === 'function';
  }
} 